import { gql } from "@apollo/client";
import {
  BASIC_CHECK_FRAGMENT,
  CHECK_STATS_FRAGMENT,
} from "@/modules/checks/queries.js";

export const BASIC_CAMPAIGN_FRAGMENT = gql`
  fragment BasicCampaignFragment on Campaign {
    id
    name
    startDate
    targetDate
    endedDate
    status
    href
    reminder {
      message
      frequency
      frequencyUnit
      daysOfWeek
      channels
      timeOfDay
      timezone
      nextOccurrence
      defaultSlackChannel
      defaultMicrosoftTeamsChannel
    }
    permissions {
      canUpdate
      canDelete
    }
  }
`;
export const CAMPAIGN_STATS_FRAGMENT = gql`
  fragment CampaignStatsFragment on Campaign {
    serviceStats {
      total
      totalSuccessful
    }
    checkStats {
      total
      totalSuccessful
    }
  }
`;

export const CAMPAIGN_STATS_HISTORY_FRAGMENT = gql`
  fragment CampaignStatsHistoryFragment on Campaign {
    campaignStatsHistory {
      timestamp
      serviceStats {
        totalSuccessful
        total
      }
    }
  }
`;

export const CAMPAIGN_OWNER_FRAGMENT = gql`
  fragment CampaignOwnerFragment on Campaign {
    owner {
      name
      href
    }
  }
`;

export const CREATE_CAMPAIGN_MUTATION = gql`
  mutation campaignCreate(
    $name: String!
    $ownerId: ID!
    $filterId: ID
    $checkIdsToCopy: [ID!]
    $projectBrief: String
    $reminder: CampaignReminderInput
  ) {
    campaignCreate(
      input: {
        name: $name
        ownerId: $ownerId
        filterId: $filterId
        projectBrief: $projectBrief
        checkIdsToCopy: $checkIdsToCopy
        reminder: $reminder
      }
    ) {
      campaign {
        ...BasicCampaignFragment
      }
      errors {
        message
        path
        title
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
`;

export const UPDATE_CAMPAIGN_MUTATION = gql`
  mutation campaignUpdate(
    $id: ID!
    $name: String
    $ownerId: ID
    $filterId: ID
    $projectBrief: String
    $reminder: CampaignReminderInput
  ) {
    campaignUpdate(
      input: {
        id: $id
        name: $name
        ownerId: $ownerId
        filterId: $filterId
        projectBrief: $projectBrief
        reminder: $reminder
      }
    ) {
      campaign {
        ...BasicCampaignFragment
      }
      errors {
        message
        path
        title
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
`;

export const SHOW_CAMPAIGN_QUERY = gql`
  query campaignShow($id: ID!) {
    account {
      campaign(id: $id) {
        ...BasicCampaignFragment
        ...CampaignStatsFragment
        ...CampaignStatsHistoryFragment
        plainId
        reportUpdatedAt
        campaignStatsUpdatedAt
        canSendReminders
        owner {
          name
          id
          href
        }

        filter {
          name
          id
          href
        }

        reminder {
          channels
          frequency
          frequencyUnit
          daysOfWeek
          timeOfDay
          timezone
        }

        autopilotInitiatives {
          id
          name
          identifier
          status
          campaignCheckIds
          services(first: 0) {
            filteredCount
          }
        }
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
  ${CAMPAIGN_STATS_FRAGMENT}
  ${CAMPAIGN_STATS_HISTORY_FRAGMENT}
`;

export const FETCH_CAMPAIGNS_QUERY = gql`
  query campaigns(
    $after: String
    $first: Int
    $sortBy: CampaignSortEnum
    $filter: [CampaignFilterInput!]
  ) {
    account {
      campaigns(
        after: $after
        first: $first
        sortBy: $sortBy
        filter: $filter
      ) {
        totalCount
        nodes {
          ...BasicCampaignFragment
          ...CampaignStatsFragment
          owner {
            name
            id
            href
          }
          filter {
            name
            id
            href
          }
        }
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
  ${CAMPAIGN_STATS_FRAGMENT}
`;

export const FETCH_CAMPAIGN_PROJECT_BRIEF_QUERY = gql`
  query campaignShowProjectBrief($id: ID!) {
    account {
      campaign(id: $id) {
        ...BasicCampaignFragment
        rawProjectBrief
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
`;

export const UPDATE_CAMPAIGN_PROJECT_BRIEF_MUTATION = gql`
  mutation campaignUpdateProjectBrief($id: ID!, $projectBrief: String) {
    campaignUpdate(input: { id: $id, projectBrief: $projectBrief }) {
      campaign {
        ...BasicCampaignFragment
        rawProjectBrief
        projectBrief
      }
      errors {
        message
        path
        title
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
`;
export const CAMPAIGN_CHECKS_QUERY = gql`
  query campaignChecks($id: ID!) {
    account {
      campaign(id: $id) {
        ...BasicCampaignFragment
        checks {
          nodes {
            ...BasicCheckFragment
            ...CheckStatsFragment
          }
        }
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
  ${BASIC_CHECK_FRAGMENT}
  ${CHECK_STATS_FRAGMENT}
`;

export const SCHEDULE_CAMPAIGN_MUTATION = gql`
  mutation campaignScheduleUpdate(
    $id: ID!
    $startDate: ISO8601DateTime!
    $targetDate: ISO8601DateTime!
  ) {
    campaignScheduleUpdate(
      input: { id: $id, startDate: $startDate, targetDate: $targetDate }
    ) {
      campaign {
        ...BasicCampaignFragment
      }
      errors {
        message
        path
        title
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
`;

export const UNSCHEDULE_CAMPAIGN_MUTATION = gql`
  mutation unscheduleCampaign($id: ID!) {
    campaignUnschedule(input: { id: $id }) {
      campaign {
        ...BasicCampaignFragment
      }
      errors {
        message
        path
        title
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
`;

export const CAMPAIGN_REPORT_BY_SERVICES_WITH_STATUS_QUERY = gql`
  query campaignReportByServices(
    $id: ID!
    $after: String
    $first: Int
    $sortBy: CampaignStatusByServiceSortEnum
    $searchTerm: String
    $overallStatus: CampaignServiceStatusEnum
  ) {
    account {
      campaign(id: $id) {
        id
        name
        statusByService(
          after: $after
          first: $first
          sortBy: $sortBy
          searchTerm: $searchTerm
          overallStatus: $overallStatus
        ) {
          filteredCount
          nodes {
            overallStatus
            service {
              id
              name
              type {
                name
                icon {
                  name
                  color
                }
              }
              href
              tier {
                name
              }
              owner {
                name
                href
                contacts {
                  id
                  type
                  displayName
                  address
                  targetHref
                }
              }
              campaignReport(campaignIds: [$id]) {
                checkResultsByCampaign {
                  nodes {
                    status
                    items {
                      nodes {
                        status
                        check {
                          id
                          name
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const CAMPAIGN_REPORT_BY_TEAMS_WITH_STATUS_QUERY = gql`
  query campaignReportByTeams(
    $id: ID!
    $after: String
    $first: Int
    $sortBy: CampaignStatusByTeamSortEnum
    $searchTerm: String
    $overallStatus: CampaignServiceStatusEnum
  ) {
    account {
      campaign(id: $id) {
        id
        name
        statusByTeam(
          after: $after
          first: $first
          sortBy: $sortBy
          searchTerm: $searchTerm
          overallStatus: $overallStatus
        ) {
          filteredCount
          nodes {
            overallStatus
            performance {
              serviceStats {
                total
                totalSuccessful
              }
            }
            team {
              id
              name
              href
              contacts {
                id
                type
                displayName
                address
                targetHref
              }
            }
          }
        }
      }
    }
  }
`;

export const CAMPAIGN_SERVICE_REPORT_FOR_TEAM_QUERY = gql`
  query campaignServiceReportForTeam($teamId: ID!, $campaignId: ID!) {
    account {
      team(id: $teamId) {
        teamCampaignPerformance(campaignIds: [$campaignId]) {
          services {
            id
            name
            href
            tier {
              name
            }
            type {
              name
              icon {
                name
                color
              }
            }
            campaignReport(campaignIds: [$campaignId]) {
              checkResultsByCampaign {
                nodes {
                  status
                  items {
                    nodes {
                      status
                      check {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const END_CAMPAIGN_MUTATION = gql`
  mutation campaignEnd($id: ID!, $checksToPromote: [CheckToPromoteInput!]) {
    campaignEnd(input: { id: $id, checksToPromote: $checksToPromote }) {
      campaign {
        ...BasicCampaignFragment
      }
      errors {
        message
        path
        title
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
`;

export const DELETE_CAMPAIGN_MUTATION = gql`
  mutation campaignDelete($id: ID!) {
    campaignDelete(input: { id: $id }) {
      errors {
        message
        path
        title
      }
    }
  }
`;

export const FETCH_CAMPAIGN_NOTIFICATION_DETAILS = gql`
  query campaignNotificationDetails($id: ID!, $hasContact: Boolean!) {
    account {
      campaign(id: $id) {
        name
        teams(hasContact: $hasContact, campaignProgress: failing) {
          totalCount
          nodes {
            team {
              name
              href
              contacts {
                type
                isDefault
              }
            }
          }
        }
      }
    }
  }
`;

export const SEND_ADHOC_REMINDER = gql`
  mutation sendReminder(
    $id: ID!
    $reminderTypes: [CampaignReminderTypeEnum!]!
    $customMessage: String
  ) {
    campaignSendReminder(
      input: {
        id: $id
        reminderTypes: $reminderTypes
        customMessage: $customMessage
      }
    ) {
      teamsBeingNotified {
        totalCount
      }
      errors {
        title
        message
        path
      }
    }
  }
`;

export const COPY_RUBRIC_CHECKS_TO_CAMPAIGN = gql`
  mutation checksCopyToCampaign($id: ID!, $checkIdsToCopy: [ID!]!) {
    checksCopyToCampaign(
      input: { campaignId: $id, checkIds: $checkIdsToCopy }
    ) {
      campaign {
        ...BasicCampaignFragment
      }
      errors {
        title
        message
        path
      }
    }
  }
  ${BASIC_CAMPAIGN_FRAGMENT}
`;

export const CREATE_INITIATIVE_FROM_CAMPAIGN_MUTATION = gql`
  mutation autopilotInitiativeCreate($campaignId: ID!, $checkIds: [ID!]!) {
    autopilotInitiativeCreate(
      input: { campaignId: $campaignId, checkIds: $checkIds }
    ) {
      autopilotInitiative {
        id
        name
        identifier
        status
      }
      errors {
        message
        path
      }
    }
  }
`;

export const CAMPAIGNS_IN_PROGRESS_WITH_CHECKS = gql`
  query campaignsInProgressChecks {
    account {
      campaigns(filter: { key: status, arg: "in_progress" }) {
        nodes {
          id
          name
          status
          startDate
          endedDate
          checks {
            nodes {
              id
              name
            }
          }
        }
      }
    }
  }
`;
